import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getProductById, fetchProducts } from '@/data/productData';
import PaymentPageSkeleton from '@/components/payment/PaymentPageSkeleton';
import PaymentNotFound from '@/components/payment/PaymentNotFound';
import PaymentForm from '@/components/payment/PaymentForm';
import { GrossCalculationResult } from '@/utils/fees/calculators/grossAmountCalculator';
import OfferFooter from '@/components/payment/OfferFooter';

const PaymentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Extract checkout Stripe account ID from URL query params if present
  const checkoutStripeAccountId = searchParams.get("stripeAccountId");
  // Extract external user_id and product_id from URL query params for external integrations
  const externalUserId = searchParams.get("user_id");
  const externalProductId = searchParams.get("product_id");

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);

      if (!id) {
        return;
      }

      try {
        // Use edge function to get product publicly (bypasses RLS)
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-public-product?id=${id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
          }
        );

        if (response.ok) {
          const productData = await response.json();
          setProduct(productData);
          setNotFound(false);
        } else {
          console.log("Product not found via edge function, falling back to local data");

          // Fall back to the original method using local data
          await fetchProducts();
          const foundProduct = await getProductById(id);

          if (!foundProduct) {
            console.log("Product not found");
            setNotFound(true);
            setProduct(null);
          } else if (foundProduct.status !== "active") {
            console.log("Product is not active:", foundProduct.status);
            setNotFound(true);
            setProduct(null);
          } else {
            setProduct(foundProduct);
            setNotFound(false);
          }
        }
      } catch (error) {
        console.error("Error loading product:", error);
        setProduct(null);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  // Create default gross calculation
  const defaultGrossCalculation: GrossCalculationResult = {
    grossAmount: product?.amount || 0,
    netAmount: product?.amount || 0,
    stripeFee: 0,
    keepMoreFee: 0,
    applicationFee: 0,
    totalFees: 0,
    raw: {
      grossAmountCents: Math.round((product?.amount || 0) * 100),
      netAmountCents: Math.round((product?.amount || 0) * 100),
      stripeFeeCents: 0,
      keepMoreFeeCents: 0,
      applicationFeeCents: 0,
    },
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="container mx-auto px-4">
        {loading ? (
          <PaymentPageSkeleton />
        ) : product ? (
          <>
            <PaymentForm
              product={product}
              country="US"
              region="CA"
              grossCalculation={defaultGrossCalculation}
              checkoutStripeAccountId={
                checkoutStripeAccountId || product.connectedAccountId
              }
              externalUserId={externalUserId || undefined}
              externalProductId={externalProductId || undefined}
            />
            <OfferFooter />
          </>
        ) : (
          <PaymentNotFound />
        )}
      </div>
    </div>
  );
};

export default PaymentPage;